import { setup } from "./HuntLib";

import { SockAPIErrorCode } from "sockapi/errors";
import { ScopeType } from "sockapi/scope-types";
import { Scope, simplifyScope } from "sockapi/scopes";
import {
  SockAPIUpdatableScopeType,
  SockAPIUpdatableScope,
  SockAPIScopeUpdatePayload,
} from "sockapi/scope-updates";
import {
  TeamPuzzleSockAPIScopeState,
  getCompletionVerb,
} from "sockapi/scope-specs/team-spec";
import {
  AdminQueueTaskType,
  getPuzzleSubmissionTaskDesc,
} from "sockapi/scope-specs/admin-queue-task-spec";
import { getPuzzleDisplayName, PuzzleData } from "sockapi/puzzle-data";
import {
  HuntNotificationType,
  HuntNotification,
} from "sockapi/client-notifications";
import { SockClient } from "sockapi/client/SockClient";
import { BrowserRawWSInterface } from "sock-server/server-interface/BrowserRawWSInterface";
import { InteractiveServerInterface } from "sock-server/server-interface/InteractiveServerInterface";
import {
  ServerInterfaceType,
  NullServerInterface,
} from "sock-server/server-interface/ServerInterface";
import settings, { mockServerOpts } from "settings";
import { useServerInteractionStore } from "stores/ServerInteractionStore";
import {
  useScopeStateStore,
  getTeamScopeStateNonReactive,
  getScopeTrackingEntryNonReactive,
  getGlobalScopeStateNonReactive,
  getScopeStateResolvableNonReactive,
} from "stores/ScopeStateStore";
import {
  useIsAdminEnabledStore,
  useAdminPreferencesStore,
} from "stores/UserPreferencesStore";
import {
  shareBrowserWidePersistentState,
  useBrowserWidePersistentStateStore,
  updateBrowserWidePersistentStateNonReactive,
} from "stores/BrowserWidePersistentState";
import { resolveRoute } from "components/urls";
import { regionNames } from "components/Region";
import { getJwt } from "getJwt";
import Globals, { showNotify, toastr } from "Globals";
import { SharedWorkerInterface } from "sock-server/server-interface/SharedWorkerInterface";
import { PuzzleType } from "sockapi/puzzle-data";

import SharedWorkerServer from "./sharedWorkerServer?sharedworker";

const setupSockClientAsync = async () => {
  const initIsAdmin =
    useAdminPreferencesStore.getState().isAdminEnabledPref &&
    settings.hasAdminAccess;
  const initIsSpectating =
    useAdminPreferencesStore.getState().isSpectatingPref &&
    settings.hasAdminAccess;

  useIsAdminEnabledStore.getState().setIsAdminEnabled(initIsAdmin);
  useIsAdminEnabledStore.getState().setIsSpectating(initIsSpectating);
  useServerInteractionStore.getState().setActiveTeamId(settings.fixedTeamId);
  shareBrowserWidePersistentState();

  const sockClient = new SockClient({
    dumpWsMessages: settings.dumpWsMessages,
    initTeamId: settings.fixedTeamId ?? undefined,
    // In posthunt mode, connect to the server as admin,
    // but make it seem like we're not admin in the UI.
    initIsAdmin: initIsAdmin || settings.isPosthunt,
    initIsSpectating,
    delay: settings.clientDelay,
    getJwtFunc: async (isAdmin, forceRegen) => {
      const teamId = settings.fixedTeamId ?? undefined;
      if (!settings.useJwt)
        return JSON.stringify({
          teamId,
          isAdmin: isAdmin ? true : undefined,
        });
      if (teamId === undefined)
        throw new Error("teamId should be fixed in jwt mode");
      return getJwt(teamId, isAdmin, forceRegen);
    },
    makeServerInterfaceFunc: async (type) => {
      // Disable cursors server this year until we find a need for it.
      if (type === ServerInterfaceType.CURSOR) return new NullServerInterface();

      // Mask out both the shared mock and mock servers entirely
      // in (non-posthunt) prod to avoid including them in the build.
      if (import.meta.env.MODE !== "production") {
        // If shared workers aren't supported, fallback to
        // the raw mock server interface.
        if (settings.useSharedWorker && window.SharedWorker !== undefined) {
          const worker = new SharedWorkerServer();
          return new SharedWorkerInterface(worker, mockServerOpts);
        } else if (settings.localMode) {
          const makeClientMockServerInterface = (
            await import("makeClientMockServerInterface")
          ).default;
          const mockServerInterface = await makeClientMockServerInterface(type);
          Globals.sockServer =
            mockServerInterface.mockServerInterface.sockServer;
          return mockServerInterface;
        }
      }

      const endpoint = ((type: ServerInterfaceType): string | null => {
        switch (type) {
          case ServerInterfaceType.MAIN:
            return settings.wsEndpoint;
          case ServerInterfaceType.CURSOR:
            return settings.cursorsWsEndpoint;
        }
      })(type);

      // Fallback in case we don't have a cursor WS.
      if (!endpoint) {
        console.warn(`[${type}] endpoint not found`);
        return new NullServerInterface();
      }

      return new InteractiveServerInterface({
        rawWsInterface: new BrowserRawWSInterface(endpoint),
      });
    },
    getScopeStateCacheTag: (scope) =>
      getScopeTrackingEntryNonReactive(scope)?.cacheTag,
    directBackendUrl: settings.directBackendUrl,
  });
  Globals.sockClient = sockClient;

  sockClient.onTeamIdChangeEvent.addListener((teamId) => {
    useServerInteractionStore.getState().setActiveTeamId(teamId);
  });

  const getIsAdmin = () => settings.hasAdminAccess && sockClient.isAdmin;

  // Notifications that might be waiting for global state to be updated
  // so that we can resolve and show them.
  const notifsQueue: HuntNotification[] = [];
  let pieceNotifs: {
    region?: string;
    roundId?: string;
    roundName?: string;
    roundLink?: string;
  }[] = [];
  const tryGetPuzzleData = (puzName: string) => {
    const teamScopeState = getTeamScopeStateNonReactive();
    if (teamScopeState === null) {
      return {
        puzData: null,
        roundData: null,
      };
    }
    const { puzzles, rounds } = teamScopeState;
    const puzData = puzzles[puzName] ?? null;
    return {
      puzData,
      roundData: puzData?.roundId ? rounds[puzData.roundId] : null,
    };
  };
  const tryGetPuzzleNotifInfo = (
    puzName: string,
    subpage: "puzzle" | "hints" = "puzzle"
  ): {
    displayName: string;
    link: string;
    roundName?: string;
    roundLink?: string;
    completionVerb: string;
    puzData: TeamPuzzleSockAPIScopeState;
  } | null => {
    const { puzData, roundData } = tryGetPuzzleData(puzName);
    const teamId = sockClient.getTeamId();
    const customTitles =
      getScopeStateResolvableNonReactive(
        ScopeType.TEAM_TITLES,
        teamId !== null
          ? {
              teamId,
            }
          : null
      )?.titles ?? null;

    if (
      puzData === null ||
      customTitles === null ||
      (puzData.type === PuzzleType.PIECE && roundData === null)
    )
      return null;
    const { slug, untitledPuzzleIndex } = puzData;
    const completionVerb = getCompletionVerb(puzData);

    let displayPuzData: TeamPuzzleSockAPIScopeState | PuzzleData = puzData;

    const shouldDisplayAdmin =
      useServerInteractionStore.getState().shouldDisplayAdmin;
    if (shouldDisplayAdmin) {
      const adminPuzData = getGlobalScopeStateNonReactive(ScopeType.HUNT_ADMIN)
        ?.puzzles[puzName];
      if (adminPuzData) displayPuzData = adminPuzData;
    }

    return {
      displayName: getPuzzleDisplayName(
        displayPuzData,
        untitledPuzzleIndex,
        customTitles[puzName],
        shouldDisplayAdmin
      ),
      link: resolveRoute({ route: subpage, params: { slug } }),
      ...(roundData
        ? {
            roundName: roundData.displayName,
            roundLink: resolveRoute({
              route: "round",
              params: { slug: roundData.slug },
            }),
          }
        : {}),
      completionVerb,
      puzData,
    };
  };

  /**
   * Pop up notifications from the notifs queue as long as we have the
   * global state synced for them.
   */
  const processNotifsQueue = (): void => {
    while (true) {
      const notif = notifsQueue[0];
      if (notif === undefined) {
        if (pieceNotifs.length === 0) {
          break;
        }
        // Merge all piece notifs belonging to the same round.
        const notifs: Record<
          string,
          {
            name: string;
            link: string;
            regions: Record<string, number>;
            noRegion: number;
          }
        > = {};
        for (const { region, roundId, roundName, roundLink } of pieceNotifs) {
          if (!roundId || !roundName || !roundLink) continue;
          notifs[roundId] ??= {
            name: roundName,
            link: roundLink,
            regions: {},
            noRegion: 0,
          };
          if (region !== undefined) {
            notifs[roundId].regions[regionNames[region]] ??= 0;
            notifs[roundId].regions[regionNames[region]] += 1;
          } else {
            notifs[roundId].noRegion += 1;
          }
        }
        for (const { name, link, regions, noRegion } of Object.values(notifs)) {
          showNotify({
            title: `New pieces in ${name}`,
            text: noRegion
              ? `You found ${noRegion} piece${noRegion > 1 ? "s" : ""}!`
              : `You found ${Object.entries(regions)
                  .map(
                    ([region, count]) =>
                      `${count} ${region} piece${count > 1 ? "s" : ""}`
                  )
                  .join(", ")}!`,
            link,
          });
        }
        pieceNotifs = [];
        break;
      }
      const notify: typeof showNotify = (data) => {
        showNotify(data);
        useScopeStateStore.getState().playAudioNotif?.(notif);
      };
      const isResolved = ((): boolean => {
        switch (notif.type) {
          case HuntNotificationType.INTERNAL: {
            const { message } = notif;
            toastr.warning(message);
            return true;
          }
          case HuntNotificationType.ADMIN_QUEUE_TASK: {
            const { taskId, taskSummary } = notif;
            // Always return something to make sure that the switch
            // statement is exhaustive.
            return ((): boolean => {
              switch (taskSummary.type) {
                case AdminQueueTaskType.HINT: {
                  notify({
                    title: "Hint request",
                    text: "A hint request was submitted.",
                    link: resolveRoute({
                      route: "admin_queue_task",
                      params: { taskId },
                    }),
                    isImportant: true,
                    shortTimeout: true,
                  });
                  return true;
                }
                case AdminQueueTaskType.PUZZLE_SUBMISSION: {
                  // Omit the default case to make typescript ensure
                  // that this is exhaustive.
                  const desc = getPuzzleSubmissionTaskDesc(
                    taskSummary.submissionType
                  );
                  notify({
                    title: `${desc} submission`,
                    text: `A ${desc} submission is awaiting review.`,
                    link: resolveRoute({
                      route: "admin_queue_task",
                      params: { taskId },
                    }),
                    isImportant: true,
                    shortTimeout: true,
                  });
                  return true;
                }
              }
            })();
          }
          case HuntNotificationType.VICTORY: {
            notify({
              title: "Congratulations!",
              text: "You’ve finished Galactic Puzzle Hunt 2025!",
              link: resolveRoute({ route: "victory" }),
              isImportant: true,
            });
            return true;
          }
          case HuntNotificationType.SUBMISSION_REJECTED: {
            const { puzName } = notif;
            const puzNotifInfo = tryGetPuzzleNotifInfo(puzName);
            if (puzNotifInfo === null) return false;
            const { displayName, link } = puzNotifInfo;
            notify({
              title: displayName,
              text: "Your submission was rejected.",
              link,
              isImportant: true,
            });
            return true;
          }
          case HuntNotificationType.HINT_ANSWERED: {
            const { puzName } = notif;
            const puzNotifInfo = tryGetPuzzleNotifInfo(puzName, "hints");
            if (puzNotifInfo === null) return false;
            const { displayName, link } = puzNotifInfo;
            notify({
              title: displayName,
              text: "Hint answered!",
              link,
              isImportant: true,
            });
            return true;
          }
          case HuntNotificationType.ERRATUM: {
            notify({
              title: "New erratum",
              text: "A new erratum has been published.",
              link: resolveRoute({ route: "errata" }),
            });
            return true;
          }
          case HuntNotificationType.SOLVE: {
            const { puzName } = notif;

            const puzNotifInfo = tryGetPuzzleNotifInfo(puzName);
            if (puzNotifInfo === null) return false;
            const {
              displayName,
              link,
              completionVerb,
              puzData: { type },
            } = puzNotifInfo;

            // Don't notify story solves.
            if (type === PuzzleType.STORY) return true;

            notify({
              title: displayName,
              text: `${completionVerb}!`,
              link,
            });
            return true;
          }
          case HuntNotificationType.UNLOCK: {
            const {
              puzData: { puzName },
            } = notif;

            const puzNotifInfo = tryGetPuzzleNotifInfo(puzName);
            if (puzNotifInfo === null) return false;
            const {
              displayName,
              link,
              puzData: { region, roundId, type },
              roundName,
              roundLink,
            } = puzNotifInfo;

            switch (type) {
              case PuzzleType.STORY:
                notify({
                  title: displayName,
                  text: "A new cutscene is available!",
                  link,
                  isImportant: true,
                });
                return true;
              case PuzzleType.PIECE:
                pieceNotifs.push({ region, roundId, roundName, roundLink });
                return true;
              case PuzzleType.PUZZLE:
                notify({
                  title: displayName,
                  text: "You unlocked a new puzzle!",
                  link,
                });
                return true;
            }
          }
          // Don't provide a default so that typescript will check
          // that this is exhaustive.
        }
      })();
      if (!isResolved) return;
      notifsQueue.shift();
    }
  };

  const clearLockedCutsceneStateIfNeeded = (scope: Scope) => {
    const ownTeamId = sockClient.getTeamId();
    switch (scope.type) {
      case ScopeType.TEAM: {
        if (scope.teamId !== ownTeamId) break;
        const teamState = getScopeStateResolvableNonReactive(ScopeType.TEAM, {
          teamId: ownTeamId,
        });
        if (teamState === null) break;

        const { puzzles } = teamState;
        const { completedStoryBeats, cutscenesState, pinnedPuzzles } =
          useBrowserWidePersistentStateStore.getState().state;

        const staleStoryBeats = Object.keys(completedStoryBeats).filter(
          (puzName) => puzzles[puzName]?.solveTime === undefined
        );
        if (staleStoryBeats.length > 0)
          updateBrowserWidePersistentStateNonReactive({
            completedStoryBeats: Object.fromEntries(
              staleStoryBeats.map((puzName) => [puzName, false])
            ),
          });

        const staleCutscenes = Object.keys(cutscenesState).filter(
          (puzName) => puzzles[puzName] === undefined
        );
        if (staleCutscenes.length > 0)
          updateBrowserWidePersistentStateNonReactive({
            cutscenesState: Object.fromEntries(
              staleCutscenes.map((puzName) => [puzName, null])
            ),
          });

        if (pinnedPuzzles !== undefined) {
          const newPinnedPuzzles = pinnedPuzzles.filter(
            (puzName) => puzzles[puzName] !== undefined
          );
          if (newPinnedPuzzles.length < pinnedPuzzles.length)
            updateBrowserWidePersistentStateNonReactive({
              pinnedPuzzles: newPinnedPuzzles,
            });
        }

        break;
      }
    }
  };

  sockClient.onScopeAssignEvent.addListener((assign) => {
    useScopeStateStore.getState().applyScopeAssign(assign);
    clearLockedCutsceneStateIfNeeded(assign.scope);
  });

  sockClient.onScopeUpdateEvent.addListener((upd) => {
    useScopeStateStore.getState().applyScopeUpdate(upd);
    clearLockedCutsceneStateIfNeeded(upd.scope);

    // Effects to trigger in response to scope updates.
    const updEffects: {
      [TScopeType in SockAPIUpdatableScopeType]?: (
        scope: Scope<TScopeType>,
        payload: SockAPIScopeUpdatePayload<TScopeType>
      ) => void;
    } = {
      [ScopeType.TEAM]: (scope, payload) => {
        if (scope.teamId !== sockClient.getTeamId()) return;

        const { unlocks, solveTimes } = payload;

        for (const [puzName, solveTime] of Object.entries(solveTimes ?? {})) {
          if (solveTime === null) continue;
          sockClient.onNotificationEvent.fire({
            type: HuntNotificationType.SOLVE,
            puzName,
          });
        }

        for (const [puzName, unlock] of Object.entries(unlocks ?? {})) {
          if (unlock === null) continue;
          sockClient.onNotificationEvent.fire({
            type: HuntNotificationType.UNLOCK,
            puzData: unlock,
          });
        }
      },
      [ScopeType.ADMIN_QUEUE]: (scope, payload) => {
        const { setTasks } = payload;
        const adminQueueState = getGlobalScopeStateNonReactive(
          ScopeType.ADMIN_QUEUE
        );
        if (adminQueueState === null)
          throw new Error("got update for uninitialized admin queue state");
        for (const [taskId, taskSummary] of Object.entries(setTasks ?? {})) {
          if (taskSummary.respondTime === undefined) {
            sockClient.onNotificationEvent.fire({
              type: HuntNotificationType.ADMIN_QUEUE_TASK,
              taskId,
              taskSummary,
            });
          }
        }
      },
    };
    const applyUpdEffect = <TScope extends SockAPIUpdatableScope>(
      scope: TScope,
      payload: SockAPIScopeUpdatePayload<TScope["type"]>
    ) => {
      const scopeType: TScope["type"] = scope.type;
      updEffects[scopeType]?.(simplifyScope<TScope["type"]>(scope), payload);
    };
    applyUpdEffect(upd.scope, upd.payload);
  });

  let processNotifsTimeout: number | undefined;
  sockClient.onNotificationEvent.addListener((notif) => {
    notifsQueue.push(notif);
    clearTimeout(processNotifsTimeout);
    // Only process notifications every 1000ms.
    processNotifsTimeout = window.setTimeout(() => processNotifsQueue(), 1000);
  });

  sockClient.onErrorEvent.addListener((err) => {
    const { errCode } = err;
    toastr.error(`Received error "${errCode}".`);
    switch (errCode) {
      case SockAPIErrorCode.TEAM_INACTIVE: {
        window.location.href = resolveRoute({ route: "index" });
        break;
      }
    }
  });

  useScopeStateStore.subscribe(({ scopeTrackingState }) => {
    sockClient.afterUpdateScopeTrackingState(scopeTrackingState);
    Globals.scopeTrackingState = scopeTrackingState;
  });

  const setIsAdmin = useServerInteractionStore.getState().setIsAdmin;
  setIsAdmin(getIsAdmin());
  sockClient.addAuthEffect(() => {
    setIsAdmin(getIsAdmin());
    return () => setIsAdmin(null);
  });

  // If admin, maintain subscriptions needed for receiving and
  // rendering admin queue notifications.
  sockClient.addConditionalSubscribeMultipleEffect(() =>
    getIsAdmin()
      ? [
          {
            type: ScopeType.HUNT_ADMIN,
          },
          {
            type: ScopeType.ADMIN_QUEUE,
          },
        ]
      : []
  );

  // Notifications require a persistent subscription to the team
  // scope to work.
  sockClient.addConditionalSubscribeMultipleEffect(() => {
    const teamId = sockClient.getTeamId();
    if (teamId === null) return [];
    return [
      {
        type: ScopeType.TEAM,
        teamId,
      },
      {
        type: ScopeType.TEAM_TITLES,
        teamId,
      },
    ];
  });

  useServerInteractionStore.getState().setSockClient(sockClient);
  await sockClient.connectAsync();
};
setupSockClientAsync().catch(console.error);

setup();
